import { Link } from 'react-router'
import { Avatar } from '../avatar/Avatar.tsx'
import { NewsKicker } from '../news-meta/NewsKicker.tsx'
import { NewsMeta } from '../news-meta/NewsMeta.tsx'
import styles from './ArticleHeader.module.css'

export interface ArticleHeaderProps {
  title: string
  /** The standfirst under the headline; usually the article's excerpt. */
  dek?: string
  coverUrl?: string | null
  kicker?: string
  author?: string
  authorAvatarUrl?: string | null
  /** A line under the author's name, e.g. their role. */
  authorRole?: string
  date?: string | null
  readTimeMinutes?: number
  tags?: string[]
  /** Makes each tag a link, e.g. to the news page filtered to that topic. */
  tagHref?: (tag: string) => string
  className?: string
}

/**
 * The top of an article: a full-bleed cover with the headline and dek set on
 * a panel across its foot, then the byline row. Without a cover the headline
 * stands on its own.
 */
export function ArticleHeader({
  title,
  dek,
  coverUrl,
  kicker,
  author,
  authorAvatarUrl,
  authorRole,
  date,
  readTimeMinutes,
  tags = [],
  tagHref,
  className,
}: ArticleHeaderProps) {
  return (
    <header className={[styles.header, coverUrl ? styles.withCover : '', className].filter(Boolean).join(' ')}>
      <div className={styles.lead}>
        {coverUrl && <img className={styles.cover} src={coverUrl} alt="" />}
        <div className={styles.panel}>
          <div className={styles.inner}>
            {kicker && <NewsKicker>{kicker}</NewsKicker>}
            <h1 className={styles.title}>{title}</h1>
            {dek && <p className={styles.dek}>{dek}</p>}
          </div>
        </div>
      </div>

      <div className={styles.byline}>
        {author && (
          <div className={styles.author}>
            <Avatar src={authorAvatarUrl ?? undefined} fallback={author} alt="" size="md" />
            <div className={styles.authorText}>
              <span className={styles.authorName}>{author}</span>
              {authorRole && <span className={styles.authorRole}>{authorRole}</span>}
            </div>
          </div>
        )}
        <NewsMeta date={date} readTimeMinutes={readTimeMinutes} />
        {tags.length > 0 && (
          <ul className={styles.tags} aria-label="Topics">
            {tags.map((tag) => (
              <li key={tag}>
                {tagHref
                  ? <Link to={tagHref(tag)} className={styles.tag}>{tag}</Link>
                  : <span className={styles.tag}>{tag}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </header>
  )
}
